import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Loader2,
  ShieldCheck,
} from "lucide-react";
import Nav from "../site/Nav";
import SEO from "../site/SEO";
import Footer from "../site/Footer";
import RevenuePath from "../site/revenue-path/RevenuePath";
import NodeGlyph from "../site/revenue-path/NodeGlyph";
import CheckoutPackages from "../site/CheckoutPackages";
import useScrollReveal from "../../hooks/useScrollReveal";
import { getAttribution } from "../../lib/attribution";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AUDIT_PATH = [
  { label: "Inquiry", state: "healthy" },
  { label: "Response", state: "friction" },
  { label: "Booking", state: "leak" },
  { label: "Payment", state: "broken" },
  { label: "Follow-Up", state: "recovered" },
];

const REVIEWS = [
  { label: "How leads contact you", state: "healthy" },
  { label: "How fast they get a response", state: "friction" },
  { label: "How booking happens", state: "leak" },
  { label: "How payment is handled", state: "broken" },
  { label: "How follow-up works", state: "leak" },
  { label: "Where prospects fall off", state: "friction" },
  { label: "What should be fixed first", state: "recovered" },
];

const RECEIVE = [
  "Where leads are entering",
  "Where they are getting stuck",
  "What should be automated",
  "What should be simplified",
  "What should be fixed first",
  "Which build path makes the most sense",
];

const HOW_IT_WORKS = [
  { n: "01", title: "You share your current path", body: "Answer a few questions about how leads contact you, book, pay, and get followed up." },
  { n: "02", title: "We review the path", body: "We map where leads enter, where they stall, and where revenue is slipping." },
  { n: "03", title: "You get the breakdown", body: "A clear revenue-path breakdown with the first fix that makes the most sense." },
];

const scrollToForm = () => {
  if (typeof document === "undefined") return;
  document.getElementById("audit-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
};

export default function RevenueLeakAuditPage() {
  useScrollReveal();

  const [form, setForm] = useState({
    business_name: "",
    website_or_profile: "",
    service_sold: "",
    leads_contact: "",
    booking: "",
    payment: "",
    follow_up: "",
    drop_off: "",
    fix_first: "",
    full_name: "",
    email: "",
    phone: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [intakeId, setIntakeId] = useState(null);

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const isValid = useMemo(
    () =>
      form.full_name.trim() &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()) &&
      form.phone.trim().length >= 7 &&
      form.business_name.trim() &&
      form.service_sold.trim() &&
      form.fix_first.trim(),
    [form],
  );

  const submit = async (e) => {
    e.preventDefault();
    if (!isValid || submitting) return;
    setSubmitting(true);
    setError(null);

    // Compose the diagnostic answers into the canonical intake's free-text
    // `main_problem` so every answer reaches GoHighLevel + the admin dashboard
    // through the existing /api/website-intake → GHL pipeline (no broken
    // integrations). service_interest is fixed to the audit so GHL workflows
    // can branch on it.
    // TODO: if/when GHL custom fields are provisioned for the individual audit
    // questions, map each field below to its own GHL custom field instead of
    // packing them into main_problem.
    const lines = [
      `Service sold: ${form.service_sold.trim()}`,
      form.website_or_profile.trim() && `Website / profile: ${form.website_or_profile.trim()}`,
      form.leads_contact.trim() && `How leads contact now: ${form.leads_contact.trim()}`,
      form.booking.trim() && `How people book: ${form.booking.trim()}`,
      form.payment.trim() && `How people pay: ${form.payment.trim()}`,
      form.follow_up.trim() && `How they follow up: ${form.follow_up.trim()}`,
      form.drop_off.trim() && `Where leads are dropping off: ${form.drop_off.trim()}`,
      `What to fix first: ${form.fix_first.trim()}`,
    ].filter(Boolean);

    try {
      const attribution = getAttribution();
      const payload = {
        full_name: form.full_name.trim(),
        business_name: form.business_name.trim() || null,
        email: form.email.trim(),
        phone: form.phone.trim(),
        service_interest: "Revenue Leak Audit",
        main_problem: lines.join("\n"),
        // Legacy enum fields intentionally omitted — backend treats them as
        // optional for the diagnostic audit form.
        utm_source: attribution.utm_source || null,
        utm_medium: attribution.utm_medium || null,
        utm_campaign: attribution.utm_campaign || null,
        utm_content: attribution.utm_content || null,
        utm_term: attribution.utm_term || null,
        gclid: attribution.gclid || null,
        fbclid: attribution.fbclid || null,
        referrer: attribution.referrer || null,
        landing_page: attribution.landing_page || null,
      };
      const response = await axios.post(`${API}/website-intake`, payload);
      setIntakeId(response?.data?.id || null);
      setSubmitted(true);
      requestAnimationFrame(() => {
        document
          .querySelector("[data-testid='audit-success']")
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : "We couldn't submit your audit request. Please check your details and try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-sand-100 text-ink min-h-screen" data-testid="revenue-leak-audit-page">
      <SEO
        page="/revenue-leak-audit"
        title="Revenue Leak Audit · The Modern Exit"
        description="The Revenue Leak Audit shows where prospects are getting stuck between inquiry, booking, payment, follow-up, and revenue visibility."
        path="/revenue-leak-audit"
      />
      <Nav />
      <main className="pt-24 sm:pt-28 md:pt-32 pb-16 px-5 sm:px-6 md:px-10">
        <div className="max-w-7xl mx-auto">
          <Link
            to="/"
            data-testid="audit-back"
            className="inline-flex items-center gap-2 text-[12.5px] text-ink-muted hover:text-clay transition-colors"
          >
            <ArrowLeft size={13} /> Back to home
          </Link>

          {/* Hero */}
          <div className="reveal mt-6 max-w-3xl">
            <p className="eyebrow">Revenue Leak Audit</p>
            <h1
              data-kinetic
              data-testid="audit-headline"
              className="font-display mt-4 sm:mt-5 text-[2.1rem] sm:text-[2.8rem] md:text-5xl lg:text-[3.4rem] leading-[1.05] tracking-[-0.02em] font-semibold text-ink"
            >
              Find where your leads are{" "}
              <span className="italic font-normal text-ink-muted">turning cold.</span>
            </h1>
            <p className="mt-5 sm:mt-6 text-base md:text-lg text-ink-muted leading-relaxed">
              The Revenue Leak Audit shows where prospects are getting stuck
              between inquiry, booking, payment, follow-up, and revenue
              visibility.
            </p>
            <button
              type="button"
              onClick={scrollToForm}
              data-testid="audit-hero-cta"
              data-magnetic
              className="mt-8 group inline-flex items-center justify-center gap-2 rounded-full bg-ink-deep text-sand-100 px-7 py-4 text-[15px] font-medium hover:bg-ink transition-colors min-h-[52px]"
            >
              Request Your Revenue Leak Audit
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
            </button>
          </div>

          {/* Signature diagnostic path */}
          <div className="reveal mt-12 sm:mt-16">
            <div className="flex items-center justify-between mb-4">
              <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink-muted">
                Live diagnostic · your path
              </span>
              <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-clay-deep">
                Leak detected → recovered
              </span>
            </div>
            <div className="border-y border-sand-300 py-8 sm:py-10 overflow-x-auto">
              <div className="min-w-[520px]">
                <RevenuePath stages={AUDIT_PATH} />
              </div>
            </div>
          </div>

          {/* What the audit reviews + Who it's for */}
          <div className="mt-14 sm:mt-20 grid lg:grid-cols-12 gap-8 lg:gap-12 items-start">
            <div className="lg:col-span-7 reveal">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-clay">
                What the audit reviews
              </p>
              <div className="mt-5 border border-sand-300 bg-sand-50 p-6 sm:p-8">
                <ul className="divide-y divide-sand-300/70">
                  {REVIEWS.map((r, idx) => (
                    <li key={r.label} data-testid={`audit-review-${idx}`} className="flex items-center gap-4 py-3.5">
                      <span className="font-mono text-[11px] text-ink-muted/70 tabular-nums w-5">
                        {String(idx + 1).padStart(2, "0")}
                      </span>
                      <span className="flex-1 text-[14.5px] sm:text-[15px] text-ink leading-snug">{r.label}</span>
                      <NodeGlyph state={r.state} size={18} />
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="lg:col-span-5 reveal">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-clay">Who it&rsquo;s for</p>
              <h2 className="font-display mt-4 text-2xl sm:text-3xl font-semibold tracking-tight text-ink leading-snug">
                Service businesses that can&rsquo;t afford slow follow-up.
              </h2>
              <p className="mt-4 text-[14.5px] sm:text-[15px] text-ink-muted leading-relaxed">
                Trainers, coaches, beauty and wellness providers, local service
                businesses, and solo operators who already have demand — but the
                backend between inquiry and payment is too manual.
              </p>
            </div>
          </div>

          {/* What you receive */}
          <div className="mt-14 sm:mt-20 reveal">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-clay">What you receive</p>
            <h2 className="font-display mt-4 text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight text-ink leading-snug max-w-2xl">
              A clear revenue-path breakdown.
            </h2>
            <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
              {RECEIVE.map((r, idx) => (
                <div key={r} data-testid={`audit-receive-${idx}`} className="rounded-2xl border border-sand-300 bg-sand-50 p-5 sm:p-6">
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-clay">
                    {String(idx + 1).padStart(2, "0")}
                  </span>
                  <p className="mt-3 text-[14.5px] leading-snug text-ink">{r}</p>
                </div>
              ))}
            </div>
          </div>

          {/* How it works + what happens after */}
          <div className="mt-14 sm:mt-20 grid lg:grid-cols-12 gap-8 lg:gap-12 items-start">
            <div className="lg:col-span-7 reveal">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-clay">How the audit works</p>
              <ol className="mt-5 space-y-3">
                {HOW_IT_WORKS.map((s) => (
                  <li key={s.n} className="flex gap-4 rounded-2xl border border-sand-300 bg-sand-50 p-5">
                    <span className="font-mono text-[12px] text-clay tabular-nums pt-0.5">{s.n}</span>
                    <div>
                      <p className="font-display text-[15px] sm:text-base font-semibold text-ink">{s.title}</p>
                      <p className="mt-1 text-[14px] text-ink-muted leading-relaxed">{s.body}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
            <div className="lg:col-span-5 reveal">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-clay">What happens after</p>
              <p className="mt-4 text-[14.5px] sm:text-[15px] text-ink-muted leading-relaxed">
                We map the simplest system needed to close the leak. If it makes
                sense, we build the website or revenue system that fixes it —
                tested before handoff. No pressure, no black-box builds.
              </p>
              <Link
                to="/packages"
                data-testid="audit-build-options"
                className="mt-6 inline-flex items-center gap-2 text-[14px] font-medium text-ink hover:text-clay transition-colors"
              >
                See the Build Options
                <ArrowRight size={15} />
              </Link>
            </div>
          </div>

          {/* The diagnostic form */}
          <div id="audit-form" className="mt-16 sm:mt-24 scroll-mt-24 max-w-3xl">
            <div className="reveal">
              <p className="eyebrow">Request the audit</p>
              <h2
                data-testid="audit-form-headline"
                className="font-display mt-4 text-[1.9rem] sm:text-[2.3rem] md:text-[2.7rem] leading-[1.06] tracking-[-0.02em] font-semibold text-ink"
              >
                {submitted ? "Audit request received." : "Tell us about your path."}
              </h2>
              <p className="mt-4 text-[14.5px] sm:text-[15px] text-ink-muted max-w-xl leading-relaxed">
                {submitted
                  ? "We'll review your current path and follow up with the next step."
                  : "The more you share, the sharper the breakdown. We review every request personally."}
              </p>
            </div>

            {submitted ? (
              <AuditSuccess intakeId={intakeId} />
            ) : (
              <form onSubmit={submit} className="mt-8 space-y-7" data-testid="audit-form" noValidate>
                <FieldGroup eyebrow="01 · Your business">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Input label="Business name" required value={form.business_name} onChange={update("business_name")} testid="audit-business-name" placeholder="Your business or brand name" autoComplete="organization" />
                    <Input label="Website or profile link" value={form.website_or_profile} onChange={update("website_or_profile")} testid="audit-website" placeholder="yoursite.com or instagram.com/you" />
                  </div>
                  <Input label="What service do you sell?" required value={form.service_sold} onChange={update("service_sold")} testid="audit-service-sold" placeholder="e.g. 1:1 personal training, lash sets, HVAC repair" />
                </FieldGroup>

                <FieldGroup eyebrow="02 · Your current path">
                  <Textarea label="How do leads contact you now?" value={form.leads_contact} onChange={update("leads_contact")} testid="audit-leads-contact" placeholder="DMs, calls, website form, referrals…" rows={2} />
                  <Textarea label="How do people book?" value={form.booking} onChange={update("booking")} testid="audit-booking" placeholder="Manually over text, a link, not yet…" rows={2} />
                  <Textarea label="How do people pay?" value={form.payment} onChange={update("payment")} testid="audit-payment" placeholder="Invoice, in person, Stripe link, cash…" rows={2} />
                  <Textarea label="How do you follow up?" value={form.follow_up} onChange={update("follow_up")} testid="audit-follow-up" placeholder="When I remember, no system yet…" rows={2} />
                </FieldGroup>

                <FieldGroup eyebrow="03 · The leak">
                  <Textarea label="Where do you think leads are dropping off?" value={form.drop_off} onChange={update("drop_off")} testid="audit-drop-off" placeholder="After the first message, before booking, after the quote…" rows={2} />
                  <Textarea label="What do you want fixed first?" required value={form.fix_first} onChange={update("fix_first")} testid="audit-fix-first" placeholder="The single thing costing you the most right now" rows={2} />
                </FieldGroup>

                <FieldGroup eyebrow="04 · Where to reach you">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Input label="Name" required value={form.full_name} onChange={update("full_name")} testid="audit-full-name" placeholder="Jane Avery" autoComplete="name" />
                    <Input label="Email" required type="email" value={form.email} onChange={update("email")} testid="audit-email" placeholder="jane@yourbusiness.com" autoComplete="email" />
                    <Input label="Phone number" required type="tel" value={form.phone} onChange={update("phone")} testid="audit-phone" placeholder="(214) 555-0123" autoComplete="tel" />
                  </div>
                </FieldGroup>

                {error && (
                  <div data-testid="audit-error" className="rounded-2xl border border-rust/40 bg-rust/5 px-4 py-3 text-[13px] text-rust">
                    {error}
                  </div>
                )}

                <div className="flex flex-col sm:flex-row sm:items-center gap-4 pt-2">
                  <button
                    type="submit"
                    disabled={!isValid || submitting}
                    data-testid="audit-submit"
                    data-magnetic
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-ink-deep text-sand-100 px-6 py-3.5 text-[14px] font-medium hover:bg-rust transition-colors min-h-[48px] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submitting ? (
                      <><Loader2 size={14} className="animate-spin" /> Sending…</>
                    ) : (
                      <>Request Your Revenue Leak Audit <ArrowRight size={14} /></>
                    )}
                  </button>
                  <p className="flex items-center gap-2 text-[12px] text-ink-muted">
                    <ShieldCheck size={12} className="text-olive" />
                    Reviewed personally — we follow up within one business day.
                  </p>
                </div>
                <p data-testid="audit-consent" className="text-[11px] leading-relaxed text-ink-muted/80 max-w-xl">
                  By submitting this form, you agree that The Modern Exit may
                  contact you by email or phone about your request.
                </p>
              </form>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function AuditSuccess({ intakeId }) {
  const bookHref = intakeId ? `/book?ref=${encodeURIComponent(intakeId)}` : "/book";
  return (
    <section data-testid="audit-success" className="mt-8 rounded-3xl border border-olive/40 bg-olive/5 p-7 sm:p-9">
      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-olive flex items-center gap-2">
        <CheckCircle2 size={12} /> Received
      </p>
      <h2 className="font-display mt-3 text-[1.6rem] sm:text-2xl font-semibold text-ink leading-snug">
        Your audit request has been received.
      </h2>
      <p data-testid="audit-success-message" className="mt-4 text-[14.5px] text-ink leading-relaxed max-w-xl">
        The Modern Exit will review your current path and follow up with the
        next step.
      </p>
      <div className="mt-7 rounded-2xl border border-clay/30 bg-sand-50 p-5 sm:p-6">
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-clay">Skip the wait</p>
        <p className="mt-2 text-[14.5px] text-ink leading-relaxed max-w-xl">
          Want to talk it through sooner? Book a consultation and we&rsquo;ll
          walk the path together.
        </p>
        <Link
          data-testid="audit-success-book"
          to={bookHref}
          className="mt-4 inline-flex items-center gap-2 rounded-full bg-ink-deep text-sand-100 px-5 py-3 text-[13px] font-medium hover:bg-rust transition-colors min-h-[44px]"
        >
          Book a Consultation <ArrowRight size={13} />
        </Link>
      </div>
      <CheckoutPackages
        group="all"
        submissionId={intakeId}
        submissionKind="lead"
        title="Know what you need? Pay securely and skip ahead."
      />
      <Link to="/" className="mt-7 inline-flex items-center gap-2 text-[13px] text-ink-muted hover:text-clay transition-colors">
        <ArrowLeft size={13} /> Back to home
      </Link>
    </section>
  );
}

/* Presentational primitives — match the existing Modern Exit intake aesthetic. */
function FieldGroup({ eyebrow, children }) {
  return (
    <fieldset className="reveal rounded-3xl border border-sand-300 bg-sand-50 p-6 sm:p-7 space-y-5">
      <legend className="px-2 font-mono text-[10px] uppercase tracking-[0.2em] text-clay">{eyebrow}</legend>
      {children}
    </fieldset>
  );
}

function Label({ children }) {
  return (
    <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink-muted">{children}</span>
  );
}

function Input({ label, testid, required, ...rest }) {
  return (
    <label className="block">
      <Label>{label}{required ? " *" : ""}</Label>
      <input
        {...rest}
        required={required}
        data-testid={testid}
        className="mt-1.5 w-full h-11 rounded-xl border border-sand-300 bg-sand-100 px-4 text-ink placeholder:text-ink-muted/50 focus:border-clay focus:outline-none focus:ring-2 focus:ring-clay/20 transition-colors"
      />
    </label>
  );
}

function Textarea({ label, testid, required, rows = 3, ...rest }) {
  return (
    <label className="block">
      <Label>{label}{required ? " *" : ""}</Label>
      <textarea
        {...rest}
        rows={rows}
        required={required}
        data-testid={testid}
        className="mt-1.5 w-full rounded-xl border border-sand-300 bg-sand-100 px-4 py-3 text-ink placeholder:text-ink-muted/50 focus:border-clay focus:outline-none focus:ring-2 focus:ring-clay/20 transition-colors leading-relaxed text-[14px]"
      />
    </label>
  );
}
